'use client'

import { useEffect, useRef, useState, type ReactNode } from 'react'

import { useTranslation } from '@/hooks/client/use-translation'
import { cx } from '@/lib/utils'

export interface TabData {
  uid: string
  title: string
  content: ReactNode | ReactNode[]
  editableProps?: Record<string, unknown>
}

export interface TabbedContentClientProps {
  tabs: TabData[]
  fullWidth?: boolean
  editableProps?: Record<string, unknown>
}

const ChevronLeft = ({ className }: { className?: string }) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    className={className}
  >
    <path d="m15 18-6-6 6-6" />
  </svg>
)

const ChevronRight = ({ className }: { className?: string }) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    className={className}
  >
    <path d="m9 18 6-6-6-6" />
  </svg>
)

export const TabbedContentClient = ({ tabs, fullWidth, editableProps }: TabbedContentClientProps) => {
  const { t } = useTranslation()
  const [activeTab, setActiveTab] = useState(0)
  const scrollContainerRef = useRef<HTMLDivElement>(null)
  const activeTabRef = useRef<HTMLButtonElement>(null)
  const [showLeftArrow, setShowLeftArrow] = useState(false)
  const [showRightArrow, setShowRightArrow] = useState(false)

  const checkForScroll = () => {
    if (scrollContainerRef.current) {
      const { scrollLeft, scrollWidth, clientWidth } = scrollContainerRef.current
      setShowLeftArrow(scrollLeft > 0)
      setShowRightArrow(scrollLeft < scrollWidth - clientWidth - 1)
    }
  }

  useEffect(() => {
    const container = scrollContainerRef.current
    if (container) {
      checkForScroll()
      container.addEventListener('scroll', checkForScroll)
      window.addEventListener('resize', checkForScroll)
      return () => {
        container.removeEventListener('scroll', checkForScroll)
        window.removeEventListener('resize', checkForScroll)
      }
    }
  }, [tabs])

  useEffect(() => {
    if (activeTabRef.current && scrollContainerRef.current) {
      const container = scrollContainerRef.current
      const tab = activeTabRef.current
      const tabLeft = tab.offsetLeft
      const tabWidth = tab.offsetWidth
      const containerWidth = container.offsetWidth
      const scrollLeft = container.scrollLeft

      if (tabLeft < scrollLeft || tabLeft + tabWidth > scrollLeft + containerWidth) {
        container.scrollTo({
          left: tabLeft - containerWidth / 2 + tabWidth / 2,
          behavior: 'smooth',
        })
      }
    }
  }, [activeTab])

  const scroll = (direction: 'left' | 'right') => {
    if (scrollContainerRef.current) {
      const container = scrollContainerRef.current
      const scrollAmount = container.clientWidth / 2
      container.scrollTo({
        left: container.scrollLeft + (direction === 'left' ? -scrollAmount : scrollAmount),
        behavior: 'smooth',
      })
    }
  }

  if (tabs.length === 0) {
    return null
  }

  return (
    <div {...editableProps} className={cx('not-prose', fullWidth && 'w-full')}>
      <div className="relative">
        {/* Left Scroll Button */}
        <div
          className={cx(
            'absolute top-0 bottom-0 left-0 z-20 flex items-center pr-2 transition-opacity duration-300',
            showLeftArrow ? 'pointer-events-auto opacity-100' : 'pointer-events-none opacity-0'
          )}
        >
          <button
            onClick={() => scroll('left')}
            data-tracking="ignore"
            className="border-default text-body hover:text-fg-brand rounded-full border bg-white p-1.5 transition-colors"
            aria-label={t('client.carousel.scrollLeft', 'Scroll left')}
          >
            <ChevronLeft className="h-4 w-4" />
          </button>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1fr)' }}>
          <div ref={scrollContainerRef} className="no-scrollbar" style={{ display: 'flex', overflowX: 'auto' }}>
            {tabs.map((tab, index) => {
              const isActive = index === activeTab
              return (
                <button
                  key={tab.uid}
                  ref={isActive ? activeTabRef : null}
                  onClick={() => setActiveTab(index)}
                  className={cx(
                    'px-4 py-2 text-sm font-medium whitespace-nowrap transition-colors',
                    isActive ? 'border-brand text-fg-brand border-b-2' : 'text-body hover:text-fg-brand'
                  )}
                  aria-current={isActive ? 'page' : undefined}
                  {...tab.editableProps}
                >
                  {tab.title}
                </button>
              )
            })}
          </div>
        </div>

        {/* Right Scroll Button */}
        <div
          className={cx(
            'absolute top-0 right-0 bottom-0 z-20 flex items-center pl-2 transition-opacity duration-300',
            showRightArrow ? 'pointer-events-auto opacity-100' : 'pointer-events-none opacity-0'
          )}
        >
          <button
            onClick={() => scroll('right')}
            data-tracking="ignore"
            className="border-default text-body hover:text-fg-brand rounded-full border bg-white p-1.5 transition-colors"
            aria-label={t('client.carousel.scrollRight', 'Scroll right')}
          >
            <ChevronRight className="h-4 w-4" />
          </button>
        </div>
      </div>

      <div className="pt-4">{tabs[activeTab]?.content}</div>
    </div>
  )
}
